// SCR-STAFF-924 — حساب‌های دستگاه (US-IAM-053، AP-IAM-05): فهرست دستگاه‌ها با نوع، شعبه، وضعیت و دامنه؛ ثبت با نوع، شعبه و نام ← کد جفت‌سازی
// ۸ رقمی با اعتبار ۱۰ دقیقه (AC-IAM-053.1؛ فقط یک‌بار نمایش)؛ کد تازه، ویرایش نام و لغو با دلیل (اعتبارنامه فوراً باطل — AC-IAM-053.2).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type DeviceAccountPairingView,
  type DeviceAccountStatus,
  type DeviceAccountType,
  type DeviceAccountView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, ListPage, LoadingState, ReasonDialog, StatusChip, toJalali, toJalaliDateTime, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { WriteNotice } from '../settings/common';
import { useBranchOptions } from './common';

export const SCR_STAFF_924 = 'SCR-STAFF-924';
export const DEVICE_ACCOUNTS_PATH = '/settings/device-accounts';

/** انواع دستگاه (AC-IAM-053.1؛ Tablet — C-AGENT-805). */
export const DEVICE_TYPES: readonly DeviceAccountType[] = ['Tablet', 'QueueDisplay', 'CallerPanel', 'Kiosk'];

const deviceKeys = {
  list: (tenantId: string | undefined) => ['identity', tenantId, 'device-accounts'] as const,
};

function statusTone(status: DeviceAccountStatus): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    case 'PendingPairing':
      return 'pending';
    case 'Revoked':
      return 'error';
    default:
      return 'inactive';
  }
}

function PairingCodeDialog({ pairing, onClose }: { pairing: DeviceAccountPairingView; onClose: () => void }) {
  const { t } = useTranslation('iam');
  const titleId = useId();
  const code = pairing.pairingCode;
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>{t('iam:device.code_title', { name: pairing.device.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: 'center' }}>
          <Typography
            component="output"
            variant="h3"
            dir="ltr"
            sx={{ fontFamily: 'monospace', letterSpacing: 4 }}
            data-testid="device-pairing-code"
          >
            {faDigits(`${code.slice(0, 4)} ${code.slice(4)}`)}
          </Typography>
          <Alert severity="info">
            {t('iam:device.code_hint', {
              until: pairing.device.pairingExpiresAt ? toJalali(pairing.device.pairingExpiresAt, { pattern: 'HH:mm' }) : '',
            })}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose} data-testid="device-code-done">
          {t('iam:device.code_done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateDeviceDialog({ onCreated, onClose }: { onCreated: (pairing: DeviceAccountPairingView) => void; onClose: () => void }) {
  const { t } = useTranslation('iam');
  const titleId = useId();
  const { tenantApi, session } = useTenantSession();
  const { branches } = useBranchOptions();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [name, setName] = useState('');
  const [type, setType] = useState<DeviceAccountType>('Tablet');
  const [branchId, setBranchId] = useState(session?.activeBranchId ?? '');
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/identity/device-accounts', {
          body: { name: name.trim(), type, branchId: branchId || null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (pairing) => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.list(session?.tenantId) });
      onCreated(pairing);
    },
  });
  const valid = name.trim().length > 0 && branchId !== '';
  return (
    <Dialog open onClose={create.isPending ? undefined : onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>{t('iam:device.add')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('iam:device.name')}
            value={name}
            required
            onChange={(event) => setName(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'device-name' } }}
          />
          <TextField
            select
            label={t('iam:device.type')}
            value={type}
            onChange={(event) => setType(event.target.value as DeviceAccountType)}
            slotProps={{ htmlInput: { 'data-testid': 'device-type' } }}
          >
            {DEVICE_TYPES.map((item) => (
              <MenuItem key={item} value={item}>
                {t(`iam:device.types.${item}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            required
            label={t('iam:device.branch')}
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'device-branch' } }}
          >
            {branches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            {t(`iam:device.scope.${type}`)}
          </Typography>
          {create.error ? <ErrorState error={create.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button variant="contained" disabled={!valid || create.isPending} onClick={() => create.mutate()} data-testid="device-create">
          {t('iam:device.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeviceRow({ device, canWrite, branchName, onPairing }: { device: DeviceAccountView; canWrite: boolean; branchName: string; onPairing: (pairing: DeviceAccountPairingView) => void }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: deviceKeys.list(session?.tenantId) });
  const reissue = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/identity/device-accounts/{id}:reissue-code', { params: { path: { id: device.id } } })),
    onSuccess: (pairing) => {
      refresh();
      onPairing(pairing);
    },
  });
  const revoke = useMutation({
    mutationFn: (reason: string) =>
      unwrap(tenantApi.POST('/api/v1/identity/device-accounts/{id}:revoke', { params: { path: { id: device.id } }, body: { reason } })),
    onSuccess: () => {
      setRevoking(false);
      refresh();
    },
  });
  const revoked = device.status === 'Revoked';
  return (
    <TableRow data-testid="device-row" data-status={device.status}>
      <TableCell>{device.name}</TableCell>
      <TableCell>{t(`iam:device.types.${device.type}`)}</TableCell>
      <TableCell>{branchName}</TableCell>
      <TableCell>
        <StatusChip tone={statusTone(device.status)} labelKey={`iam:device.status.${device.status}`} />
      </TableCell>
      <TableCell>{device.lastSeenAt ? toJalaliDateTime(device.lastSeenAt) : '—'}</TableCell>
      <TableCell>
        {canWrite && !revoked ? (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={() => reissue.mutate()} disabled={reissue.isPending} data-testid="device-reissue">
              {t('iam:device.reissue')}
            </Button>
            <Button size="small" color="error" onClick={() => setRevoking(true)} data-testid="device-revoke">
              {t('iam:device.revoke')}
            </Button>
          </Stack>
        ) : null}
        {reissue.error ? <ErrorState error={reissue.error} compact /> : null}
        <ReasonDialog
          open={revoking}
          titleKey="iam:device.revoke_title"
          messageKey="iam:device.revoke_message"
          confirmKey="iam:device.revoke"
          busy={revoke.isPending}
          error={revoke.error}
          onConfirm={(reason) => revoke.mutate(reason)}
          onCancel={() => setRevoking(false)}
        />
      </TableCell>
    </TableRow>
  );
}

export function DeviceAccountsPage() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('iam.device_account.manage');
  const { branches } = useBranchOptions();
  const [creating, setCreating] = useState(false);
  const [pairing, setPairing] = useState<DeviceAccountPairingView | null>(null);
  const list = useQuery({
    queryKey: deviceKeys.list(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/device-accounts', { params: { query: {} } })),
  });
  const branchName = (id: string) => branches.find((branch) => branch.id === id)?.name ?? '—';

  return (
    <ListPage
      screenId={SCR_STAFF_924}
      titleKey="iam:device.title"
      subtitleKey="iam:device.subtitle"
      actions={
        write.canWrite ? (
          <Button variant="contained" onClick={() => setCreating(true)} data-testid="device-add">
            {t('iam:device.add')}
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={2}>
        <WriteNotice write={write} />
        <Paper variant="outlined" sx={{ p: 2 }}>
          {list.isPending ? <LoadingState rows={3} /> : null}
          {list.error ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
          {list.data && list.data.items.length === 0 ? <Typography color="text.secondary">{t('iam:device.empty')}</Typography> : null}
          {list.data && list.data.items.length > 0 ? (
            <Table size="small" aria-label={t('iam:device.title')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('iam:device.name')}</TableCell>
                  <TableCell>{t('iam:device.type')}</TableCell>
                  <TableCell>{t('iam:device.branch')}</TableCell>
                  <TableCell>{t('iam:device.state')}</TableCell>
                  <TableCell>{t('iam:device.last_seen')}</TableCell>
                  <TableCell>{t('iam:device.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.data.items.map((device) => (
                  <DeviceRow key={device.id} device={device} canWrite={write.canWrite} branchName={branchName(device.branchId)} onPairing={setPairing} />
                ))}
              </TableBody>
            </Table>
          ) : null}
        </Paper>
        <Typography variant="body2" color="text.secondary">
          {t('iam:device.pair_hint', { path: '/device' })}
        </Typography>
      </Stack>
      {creating ? (
        <CreateDeviceDialog
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            setPairing(created);
          }}
        />
      ) : null}
      {pairing ? <PairingCodeDialog pairing={pairing} onClose={() => setPairing(null)} /> : null}
    </ListPage>
  );
}
